import { useState, useEffect } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { Megaphone, Eye, EyeOff, Mail, Lock } from 'lucide-react'
import { Card, Button, Input, LoadingSpinner } from '../components/common/UI'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../hooks/useUtils'
import { validators } from '../utils/validation'
import PhoneInput from 'react-phone-number-input'
import { isValidPhoneNumber } from 'libphonenumber-js'
import 'react-phone-number-input/style.css'

type AuthMode = 'login' | 'register' | 'verify' | 'forgot-password' | 'reset-password' | 'account-details'

export default function Auth() {
  const location = useLocation()
  const navigate = useNavigate()
  const { showSuccess, showError, showWarning, showInfo } = useToast()
  const auth = useAuth()
  const [mode, setMode] = useState<AuthMode>(
    location.pathname === '/merchant/register' ? 'register' : 'login'
  )
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPasswordField, setShowPasswordField] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [resetCode, setResetCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [profileData, setProfileData] = useState({ fullName: '', phoneNumber: '' })
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileError, setProfileError] = useState('')

  useEffect(() => {
    if (!auth.loading) {
      if (auth.isAuthenticated && auth.user && !auth.needsProfileCompletion) {
        navigate('/merchant/dashboard', { replace: true })
      }
      if (auth.needsProfileCompletion && auth.isAuthenticated && mode !== 'account-details') {
        setMode('account-details')
      }
    }
  }, [auth.user, auth.isAuthenticated, auth.needsProfileCompletion, auth.loading, navigate, mode])

  useEffect(() => {
    if (location.pathname === '/merchant/register') {
      setMode('register')
    } else if (location.pathname === '/merchant/login') {
      setMode('login')
    }
  }, [location.pathname])

  const handleProfileComplete = async (e: React.FormEvent) => {
    e.preventDefault()
    setProfileError('')

    if (!profileData.fullName.trim()) {
      setProfileError('Full name is required')
      return
    }

    if (profileData.fullName.trim().length < 2) {
      setProfileError('Full name must be at least 2 characters')
      return
    }

    if (profileData.phoneNumber && !isValidPhoneNumber(profileData.phoneNumber)) {
      setProfileError('Please enter a valid phone number')
      return
    }

    setProfileLoading(true)
    try {
      const result = await auth.completeProfile({
        fullName: profileData.fullName.trim(),
        phoneNumber: profileData.phoneNumber
      })

      if (result.success) {
        showSuccess('Profile Created', 'Welcome to BlastTime!')
        navigate('/merchant/dashboard', { replace: true })
      } else {
        setProfileError(result.error || 'Failed to complete profile')
      }
    } catch (error) {
      setProfileError('Failed to complete profile. Please try again.')
    } finally {
      setProfileLoading(false)
    }
  }

  const handleSocialSignIn = async (provider: 'Google' | 'Facebook') => {
    setErrors({})
    const result = await auth.signInWithRedirect(provider)
    if (!result.success && result.error) {
      setErrors({ general: result.error })
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    const newErrors: Record<string, string> = {}
    if (!email) newErrors.email = 'Email is required'
    else if (!validators.email(email)) newErrors.email = 'Invalid email'
    if (!password) newErrors.password = 'Password is required'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setLoading(true)
    try {
      const result = await auth.signIn(email, password)
      
      if (!result.success) {
        if (result.errorName === 'UserNotConfirmedException') {
          setMode('verify')
          await auth.resendSignUpCode(email)
          showWarning('Email Not Verified', 'Verification code sent to your email.')
        } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
          setMode('verify')
          showInfo('Email Verification Required', 'Please check your email for the verification code.')
        } else {
          setErrors({ general: result.error || 'Invalid credentials' })
        }
        return
      }

      showSuccess('Login Successful', 'Welcome back!')
    } catch (error: any) {
      setErrors({ general: error.message || 'An unexpected error occurred' })
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    const newErrors: Record<string, string> = {}
    if (!email) newErrors.email = 'Email is required'
    else if (!validators.email(email)) newErrors.email = 'Invalid email'
    
    if (!password) newErrors.password = 'Password is required'
    else {
      const passwordCheck = validators.password(password)
      if (!passwordCheck.isValid) {
        newErrors.password = Object.values(passwordCheck.errors).find(e => e) || 'Invalid password'
      }
    }
    
    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setLoading(true)
    try {
      const signUpResult = await auth.signUp({ email, password })

      if (!signUpResult.success) {
        setErrors({ general: signUpResult.error || 'Could not create account' })
        return
      }

      setMode('verify')
      showSuccess('Registration Successful', 'Please check your email for verification code.')
    } catch (error: any) {
      setErrors({ general: error.message || 'Could not create account' })
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      const result = await auth.confirmSignUp(email, verificationCode)
      
      if (!result.success) {
        showError('Verification Failed', result.error || 'Invalid verification code')
        setLoading(false)
        return
      }
      
      showSuccess('Email Verified', 'Signing you in...')

      await new Promise(resolve => setTimeout(resolve, 500))

      const signInResult = await auth.signIn(email, password)
      
      if (signInResult.success) {
        showSuccess('Signed In', 'Please complete your profile.')
      } else {
        showError('Sign In Failed', 'Please try logging in manually.')
        setMode('login')
      }
    } catch (error: any) {
      showError('Verification Failed', error.message || 'An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleResendCode = async () => {
    setLoading(true)
    try {
      const result = await auth.resendSignUpCode(email)
      if (result.success) {
        showSuccess('Code Resent', 'A new verification code has been sent to your email.')
      } else {
        showError('Resend Failed', result.error || 'Could not resend code')
      }
    } catch (error: any) {
      showError('Resend Failed', error.message || 'Could not resend code')
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const result = await auth.resetPassword(email)
      if (result.success) {
        setMode('reset-password')
        showSuccess('Reset Code Sent', 'Please check your email for the password reset code.')
      } else {
        showError('Request Failed', result.error || 'Could not send reset code')
      }
    } catch (error: any) {
      showError('Request Failed', error.message || 'Could not send reset code')
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword !== confirmPassword) {
      showError('Password Mismatch', 'Passwords do not match')
      return
    }

    setLoading(true)
    try {
      const result = await auth.confirmResetPassword(email, resetCode, newPassword)
      if (result.success) {
        setResetCode('')
        setNewPassword('')
        setConfirmPassword('')
        setMode('login')
        showSuccess('Password Reset', 'Your password has been reset. Please login.')
      } else {
        showError('Reset Failed', result.error || 'Could not reset password')
      }
    } catch (error: any) {
      showError('Reset Failed', error.message || 'Could not reset password')
    } finally {
      setLoading(false)
    }
  }

  if (auth.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (auth.needsProfileCompletion && auth.isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <Card className="max-w-md w-full p-8">
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-gray-900">Complete Your Profile</h2>
            <p className="mt-2 text-sm text-gray-600">
              Just one more step to start advertising on BlastTime
            </p>
          </div>

          {profileError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {profileError}
            </div>
          )}

          <form onSubmit={handleProfileComplete} className="space-y-6">
            <Input
              label="Full Name *"
              type="text"
              value={profileData.fullName}
              onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
              placeholder="Enter your full name"
              disabled={profileLoading}
              required
            />

            <div>
              <label className="block text-sm font-medium mb-1">
                Phone Number (Optional)
              </label>
              <PhoneInput
                international
                defaultCountry="IN"
                value={profileData.phoneNumber}
                onChange={(value) => setProfileData({ ...profileData, phoneNumber: value || '' })}
                disabled={profileLoading}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Your merchant account will be created after submitting this form.
              </p>
            </div>

            <Button type="submit" className="w-full" loading={profileLoading}>
              Create Merchant Account
            </Button>
          </form>
        </Card>
      </div>
    )
  }

  if (mode === 'verify' || mode === 'forgot-password' || mode === 'reset-password') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <Card className="max-w-md w-full p-8">
          <div>
            <h2 className="text-center text-3xl font-extrabold text-gray-900">
              {mode === 'verify' && 'Verify your email'}
              {mode === 'forgot-password' && 'Reset your password'}
              {mode === 'reset-password' && 'Create new password'}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {mode === 'verify' && `We sent a verification code to ${email}`}
              {mode === 'forgot-password' && 'Enter your email to receive a reset code'}
              {mode === 'reset-password' && 'Enter the code from your email'}
            </p>
          </div>

          {mode === 'verify' && (
            <form className="mt-8 space-y-6" onSubmit={handleVerifyEmail}>
              <Input
                label="Verification Code"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                required
              />
              <Button type="submit" loading={loading} className="w-full">
                Verify Email
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={loading}
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  Resend code
                </button>
              </div>
            </form>
          )}

          {mode === 'forgot-password' && (
            <form className="mt-8 space-y-6" onSubmit={handleForgotPassword}>
              <Input
                label="Email"
                type="email"
                icon={Mail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Button type="submit" loading={loading} className="w-full">
                Send Reset Code
              </Button>
              <div className="text-center text-sm">
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="text-blue-600 hover:text-blue-500"
                >
                  Back to login
                </button>
              </div>
            </form>
          )}

          {mode === 'reset-password' && (
            <form className="mt-8 space-y-6" onSubmit={handleResetPassword}>
              <Input
                label="Reset Code"
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                placeholder="Enter code from email"
                required
              />
              <Input
                label="New Password"
                type="password"
                icon={Lock}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <Input
                label="Confirm New Password"
                type="password"
                icon={Lock}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <Button type="submit" loading={loading} className="w-full">
                Reset Password
              </Button>
              <div className="text-center text-sm">
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="text-blue-600 hover:text-blue-500"
                >
                  Back to login
                </button>
              </div>
            </form>
          )}
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-blue-600 to-blue-800 items-center justify-center p-12">
        <div className="max-w-xl">
          <Megaphone className="w-20 h-20 text-white mb-8" />
          <h1 className="text-4xl font-bold text-white mb-4">BlastTime Merchant Platform</h1>
          <p className="text-xl text-blue-100 mb-8">Reach 50,000+ active users with targeted advertising campaigns</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <Card className="p-8">
            <h2 className="text-3xl font-bold mb-2">{mode === 'login' ? 'Welcome back' : 'Create your account'}</h2>
            <p className="text-gray-600 mb-8">{mode === 'login' ? 'Sign in to your merchant account' : 'Start advertising to thousands'}</p>
            
            {mode === 'login' && (
              <form onSubmit={handleLogin} className="space-y-6">
                {errors.general && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    {errors.general}
                  </div>
                )}

                <div className="space-y-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full py-3" 
                    onClick={() => handleSocialSignIn('Google')}
                    disabled={loading}
                  >
                    Continue with Google
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full py-3" 
                    onClick={() => handleSocialSignIn('Facebook')}
                    disabled={loading}
                  >
                    Continue with Facebook
                  </Button>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Or continue with email</span>
                  </div>
                </div>

                <Input 
                  label="Email" 
                  type="email" 
                  icon={Mail} 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={errors.email} 
                  disabled={loading} 
                />

                <div className="relative">
                  <Input 
                    label="Password" 
                    type={showPasswordField ? 'text' : 'password'} 
                    icon={Lock}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={errors.password} 
                    disabled={loading} 
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPasswordField(!showPasswordField)}
                    className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswordField ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setMode('forgot-password')}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Forgot password?
                  </button>
                </div>

                <Button type="submit" className="w-full" loading={loading}>
                  Sign In
                </Button>

                <div className="text-center text-sm">
                  <span className="text-gray-600">Don't have an account? </span>
                  <Link to="/merchant/register" className="text-blue-600 hover:text-blue-700 font-medium">
                    Sign up
                  </Link>
                </div>
              </form>
            )}
            
            {mode === 'register' && (
              <form onSubmit={handleRegister} className="space-y-6">
                {errors.general && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    {errors.general}
                  </div>
                )}

                <div className="space-y-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full py-3"
                    onClick={() => handleSocialSignIn('Google')}
                    disabled={loading}
                  >
                    Continue with Google
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full py-3"
                    onClick={() => handleSocialSignIn('Facebook')}
                    disabled={loading}
                  >
                    Continue with Facebook
                  </Button>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Or continue with email</span>
                  </div>
                </div>

                <Input 
                  label="Email" 
                  type="email" 
                  icon={Mail} 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={errors.email}
                  disabled={loading}
                />

                <div className="relative">
                  <Input 
                    label="Password" 
                    type={showPasswordField ? 'text' : 'password'} 
                    icon={Lock}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={errors.password}
                    disabled={loading}
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPasswordField(!showPasswordField)}
                    className="absolute right-3 top-8 text-gray-400"
                  >
                    {showPasswordField ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                <Input 
                  label="Confirm Password" 
                  type={showPasswordField ? 'text' : 'password'} 
                  icon={Lock}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={errors.confirmPassword}
                  disabled={loading}
                />

                <Button type="submit" className="w-full" loading={loading}>
                  Create Account
                </Button>

                <div className="text-center text-sm">
                  <span className="text-gray-600">Already have an account? </span>
                  <Link to="/merchant/login" className="text-blue-600 hover:text-blue-700 font-medium">
                    Sign in
                  </Link>
                </div>
              </form>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}